import { Event, normalizeEventType } from '../types/event';
import { CardEvent } from '../components/EventCard/EventCard';
import { EventImageType } from '../types/events';

// Import event card images
import ceuEventImage from '../assets/backgroundImages/eventpageCardImages/CEU.png';
import dealerTrainingEventImage from '../assets/backgroundImages/eventpageCardImages/dealer-training.png';
import publicIntroClassEventImage from '../assets/backgroundImages/eventpageCardImages/public-intro-class.png';
import publicOwnershipExperienceEventImage from '../assets/backgroundImages/eventpageCardImages/public-ownership-experience.png';
import specialEventImage from '../assets/backgroundImages/eventpageCardImages/special-event.png';
import i18n from '@/i18n';

const getEventImageType = (event: Event): EventImageType => {
  // Handle case where type/subtype might be undefined
  const type = event.type?.toLowerCase() || '';
  const subtype = event.eventSubtype?.toLowerCase() || '';

  // First check eventSubtype as it's more specific
  if (subtype.includes('ceu')) return 'ceu';
  if (subtype.includes('dealer') || subtype.includes('training')) return 'dealer-training';
  if (subtype.includes('public') && subtype.includes('introduction')) return 'public-intro-class';
  if (subtype.includes('ownership')) return 'public-ownership-experience';

  // Fallback to checking type if subtype didn't match
  if (type.includes('ceu')) return 'ceu';
  if (type.includes('dealer') || type.includes('training')) return 'dealer-training';
  if (type.includes('public') && type.includes('introduction')) return 'public-intro-class';
  if (type.includes('ownership')) return 'public-ownership-experience';
  return 'special-event';
};

const getEventImage = (imageType: EventImageType): string => {
  switch (imageType) {
    case 'ceu':
      return ceuEventImage;
    case 'dealer-training':
      return dealerTrainingEventImage;
    case 'public-intro-class':
      return publicIntroClassEventImage;
    case 'public-ownership-experience':
      return publicOwnershipExperienceEventImage;
    default:
      return specialEventImage;
  }
};

// Format showroom name from all caps to title case
const formatShowroomName = (showroom: string): string => {
  if (!showroom) return '';
  
  return showroom
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const adaptEventToCardEvent = (event: any): CardEvent => {
  // Handle both old and new API response formats
  const id = event.id || event.appointmentId || `event-${Math.random().toString(36).substring(2, 9)}`;

  // Get current language
  const currentLanguage = i18n.language || 'en';
  const isFrench = currentLanguage.startsWith('fr');

  // Handle case where startDate might be missing
  let formattedDate = '';
  let formattedTime = '';

  if (event.startDate) {
    const startDate = new Date(event.startDate);

    // Use language-specific locale for date/time formatting
    const locale = isFrench ? 'fr-CA' : 'en-US';

    formattedDate = startDate.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Capitalize each word in the date (for both weekday and month)
    if (formattedDate) {
      formattedDate = formattedDate.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }

    formattedTime = startDate.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } else {
    formattedDate = 'Date TBD';
    formattedTime = 'Time TBD';
  }

  // Safely get event image type even if event.type is undefined
  const imageEventType = getEventImageType(event);

  // Map event subtype to form config key
  let formType = 'special-event'; // Default form type from eventFormConfig.ts

  // First check if it's a CEU event (either by type or subtype)
  const eventTypeLower = event.type?.toLowerCase();
  const eventSubtypeLower = event.eventSubtype?.toLowerCase();


  if (eventTypeLower === 'ceu' || eventSubtypeLower === 'ceu') {
    formType = 'event-ceu';
  } else if (event.eventSubtype) {

    switch (event.eventSubtype) {
      case 'Public Ownership Experience':
        formType = 'event-public-ownership-experience';  // Match the key in eventFormConfig.ts
        break;
      case 'Dealer Training':
        formType = 'event-dealer-training';
        break;
      case 'Public Introduction Class':
        formType = 'event-public-intro-class';
        break;
      default:
        formType = 'special-event';
    }
  } else if (event.name?.toLowerCase().includes('public introduction class')) {
    formType = 'event-public-intro-class';
  }

  return {
    id: id.toString(),
    remoteObjectId: event.remoteObjectId || event.appointmentId || '',
    title: event.name ? event.name.toUpperCase() : 'UNTITLED EVENT',
    titleFr: event.nameFr ? event.nameFr.toUpperCase() : '',
    type: normalizeEventType(event.eventType || (event.isMaroline ? 'Virtual' : null)),
    duration: event.duration ? `${event.duration}${event.durationUnit ? ' ' + event.durationUnit : 'min'}` : '60min',
    description: event.description || '',
    descriptionFr: event.descriptionFr || '',
    date: formattedDate,
    time: formattedTime,
    location: typeof event.location === 'string' ? event.location : (event.location?.address ? `${event.location.address}, ${event.location.city}` : event.city || ''),
    city: event.city || (typeof event.location === 'string' ? event.location : event.location?.city) || '',
    showroom: formatShowroomName(event.showroom || event.ultimateParent || '') as any,
    imageUrl: getEventImage(imageEventType),
    imageEventType,
    formType
  };
};
